
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, CalendarHeart, Check, Clock, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { api } from "@/lib/api";
import { toast } from "sonner";
// Cargamos el script oficial directamente en la ventana del navegador
if (typeof window !== 'undefined' && !document.getElementById('mp-script')) {
  const script = document.createElement('script');
  script.id = 'mp-script';
  script.src = 'https://sdk.mercadopago.com/js/v2';
  script.type = 'text/javascript';
  document.head.appendChild(script);
}


const LOGO_URL = "https://customer-assets.emergentagent.com/job_unified-calendar-app-2/artifacts/7wohqirg_Texto%20del%20pa%CC%81rrafo.jpeg";

const DAY_NAMES = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
const MONTH_NAMES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

// Clinical form options
const HEARD_FROM = ["Instagram", "Facebook", "Recomendación", "Otro"];
const CONDITIONS = [
  "Acné", "Artritis", "Asma", "Trastorno sanguíneo", "Cáncer/Quimioterapia", "Dermatitis",
  "Diabetes", "Eczema", "Epilepsia/Convulsiones", "Herpes labial", "Afección cardíaca",
  "Herpes", "Hepatitis", "Hipertensión", "VIH/SIDA", "Hiperpigmentación", "Hipopigmentación",
  "Histerectomía", "Trastornos inmunitarios", "Insomnio", "Cicatrices queloides",
  "Presión arterial baja", "Lupus", "Insuficiencia orgánica", "Clavos/placas metálicas",
  "Flebitis (coágulos)", "Embarazada/Lactante", "Trastorno convulsivo",
  "Enfermedades de la piel", "Seborrea", "Trasplante", "Verrugas",
];
const SKIN_CONCERNS = [
  "Acné", "Puntos negros", "Piel seca", "Piel grasa", "Piel apagada",
  "Líneas finas y arrugas", "Ojeras", "Manchas oscuras", "Melasma", "Daños por sol",
  "Manchas de la edad", "Cicatrices", "Vellos encarnados", "Quemaduras por afeitado",
  "Eczema", "Poros dilatados", "Enrojecimiento", "Piel fina", "Hinchazón debajo de los ojos",
  "Tono desigual", "Textura desigual", "Envejecimiento prematuro", "Puntos blancos",
  "Vello facial excesivo", "Enfermedades de la piel", "Rosácea", "Queratosis pilaris",
];
const ALLERGY_INGREDIENTS = [
  "Productos cosméticos", "Medicamentos", "Alimentos", "Animales", "Protector solar",
  "Aceites esenciales", "Lociones", "Mariscos", "Polen", "Exfoliantes", "Fragancia",
  "Látex", "Aspirina",
];
const SKIN_TYPES = ["Piel normal", "Piel seca", "Piel grasa", "Piel mixta"];
const SKIN_STATES = ["Sensible", "Deshidratada", "Se siente envejecida", "Manchada", "Dañada por el sol", "Congestionada"];
const RECENT_TREATMENTS = ["Microdermoabrasión", "Tinte de pestañas", "Microagujas", "Peeling químico", "Tinte de cejas", "Depilación facial"];

const INITIAL_FORM = {
  // Required for booking
  client_name: "", client_phone: "", client_email: "",
  // Demographics
  birthdate: "", age: "", gender: "", address: "", city: "", emergency_contact: "",
  marketing_optin: false,
  heard_from: "", heard_from_other: "",
  // Medical history
  takes_medication: "", medication_detail: "",
  has_allergies: "", allergies_detail: "",
  recent_surgery: "", surgery_detail: "",
  pregnant: "", birth_control: "", hormone_therapy: "",
  // New medical history items
  isotretinoin_recent: "", isotretinoin_detail: "",
  has_implants: "", implants_detail: "",
  // Multi-selects
  conditions: [], conditions_other: "",
  skin_concerns: [],
  allergy_ingredients: [], allergy_ingredients_other: "",
  skin_type: "",
  skin_state: [],
  // Conditional questions on concerns
  acne_age: "", acne_location: "", acne_treatments: "",
  manchas_cause: [], manchas_cause_other: "",
  manchas_melasma_family: "", manchas_worse_summer: "",
  // Routine
  routine_makeup_remover: "", routine_cleanser: "", routine_serum: "",
  routine_exfoliant: "", routine_sunscreen: "", routine_other: "",
  // Habits
  smokes_drinks: "",
  has_injections: "", injections_detail: "",
  recent_treatments: [],
  last_aesthetic_date: "",
  // Lifestyle scales
  sleep_quality: "", stress_level: "", physical_activity: "", sun_exposure: "",
  expectations: "",
  notes: "",
  consent: false,
};

function startOfMonth(d) {
  const x = new Date(d);
  x.setDate(1);
  x.setHours(0, 0, 0, 0);
  return x;
}
function isoDate(d) {
  const pad = (n) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Reusable yes/no radio
function YesNo({ value, onChange, name }) {
  return (
    <RadioGroup value={value} onValueChange={onChange} className="flex gap-4 mt-1.5">
      <div className="flex items-center gap-2">
        <RadioGroupItem value="si" id={`${name}-si`} className="border-[#B5704A] text-[#B5704A]" />
        <Label htmlFor={`${name}-si`} className="text-sm cursor-pointer">Sí</Label>
      </div>
      <div className="flex items-center gap-2">
        <RadioGroupItem value="no" id={`${name}-no`} className="border-[#B5704A] text-[#B5704A]" />
        <Label htmlFor={`${name}-no`} className="text-sm cursor-pointer">No</Label>
      </div>
    </RadioGroup>
  );
}

// Reusable multi-checkbox grid
function CheckGrid({ options, selected, onChange, name }) {
  const toggle = (opt) => {
    if (selected.includes(opt)) onChange(selected.filter((s) => s !== opt));
    else onChange([...selected, opt]);
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-2">
      {options.map((opt) => (
        <label key={opt} className="flex items-center gap-2 text-sm text-[#3A2418] cursor-pointer hover:text-[#B5704A] transition-colors">
          <Checkbox
            checked={selected.includes(opt)}
            onCheckedChange={() => toggle(opt)}
            className="border-[#B5704A] data-[state=checked]:bg-[#B5704A] data-[state=checked]:border-[#B5704A]"
            data-testid={`${name}-${opt}`}
          />
          <span>{opt}</span>
        </label>
      ))}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h3 className="font-display text-2xl font-[500] text-[#3A2418] mt-2 mb-4 pb-2 border-b border-[#E8DFD2]">
      {children}
    </h3>
  );
}

export default function Booking() {
  const [searchParams] = useSearchParams();
  const preselectSlug = searchParams.get("servicio");
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [service, setService] = useState(null);
  const [month, setMonth] = useState(startOfMonth(new Date()));
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedTime, setSelectedTime] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentRedirectError, setPaymentRedirectError] = useState(null);
  const [depositStatus, setDepositStatus] = useState({ enabled: false, amount_clp: 0, loading: true });
  const [showPaymentStep, setShowPaymentStep] = useState(false);
  const [paymentAppointmentId, setPaymentAppointmentId] = useState(null);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    api
      .get("/payments/deposit/status")
      .then(({ data }) => setDepositStatus({ enabled: data.enabled, amount_clp: data.amount_clp, loading: false }))
      .catch(() => setDepositStatus({ enabled: false, amount_clp: 0, loading: false }));

    api.get("/public/services").then(({ data }) => {
      setServices(data.services);
      if (preselectSlug) {
        const found = data.services.find((s) => s.slug === preselectSlug);
        if (found) {
          setService(found);
          setStep(2);
        }
      }
    });
  }, [preselectSlug]);

  useEffect(() => {
    if (!selectedDate || !service) return;
    setLoadingSlots(true);
    api
      .get("/public/availability", { params: { date: isoDate(selectedDate), service: service.slug } })
      .then(({ data }) => setSlots(data.slots))
      .finally(() => setLoadingSlots(false));
  }, [selectedDate, service]);

  const next = () => {
    setShowPaymentStep(false);
    setStep((s) => Math.min(s + 1, 4));
  };
  const back = () => {
    setShowPaymentStep(false);
    setStep((s) => Math.max(s - 1, 1));
  };
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Calendar grid
  const monthDays = (() => {
    const first = startOfMonth(month);
    const startWeekday = first.getDay();
    const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(month.getFullYear(), month.getMonth(), d));
    return cells;
  })();

  const todayIso = isoDate(new Date());
  const isPast = (d) => d && isoDate(d) < todayIso;
  const isSunday = (d) => d && d.getDay() === 0;
  const isDisabled = (d) => isPast(d) || isSunday(d);

  const createMercadoPagoPreference = async (appointmentId) => {
    setPaymentRedirectError(null);
    setPaymentProcessing(true);
    try {
      const { data } = await api.post("/payments/mp/create-preference", {
        appointment_id: appointmentId,
      });
      const initPoint = data.init_point || data.sandbox_init_point;
      if (!initPoint) {
        throw new Error("No se pudo iniciar el pago en Mercado Pago.");
      }
      window.location.href = initPoint;
    } catch (err) {
      console.error("Mercado Pago checkout error", err);
      setPaymentRedirectError(err.response?.data?.detail || err.message || "No se pudo iniciar el pago. Intenta nuevamente.");
      throw err;
    } finally {
      setPaymentProcessing(false);
    }
  };

  const retryPayment = async () => {
    if (!paymentAppointmentId) {
      setPaymentRedirectError("No se encontró la reserva para reintentar el pago. Refresca la página e intenta nuevamente.");
      return;
    }
    if (!depositStatus.amount_clp || depositStatus.amount_clp < 1000) {
      setPaymentRedirectError(`Monto del depósito inválido (${depositStatus.amount_clp}). Verifica la configuración del pago.`);
      return;
    }
    setPaymentRedirectError(null);
    setSubmitting(true);
    try {
      await createMercadoPagoPreference(paymentAppointmentId);
    } catch (err) {
      // Error already set inside createMercadoPagoPreference
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.client_name || !form.client_phone) {
      toast.error("Por favor ingresa tu nombre y teléfono");
      return;
    }
    if (!form.client_email) {
      toast.error("El correo electrónico es obligatorio para enviarte la confirmación");
      return;
    }
    // Basic email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.client_email)) {
      toast.error("Por favor ingresa un correo electrónico válido");
      return;
    }
    if (!form.consent) {
      toast.error("Debes aceptar la declaración de consentimiento");
      return;
    }

    if (showPaymentStep && paymentAppointmentId) {
      await retryPayment();
      return;
    }

    setSubmitting(true);
    setPaymentRedirectError(null);
    try {
      const {
        client_name, client_phone, client_email,
        birthdate, age, gender, address, city, emergency_contact, notes,
        ...clinical
      } = form;
      const { data } = await api.post("/public/bookings", {
        service: service.slug,
        date: isoDate(selectedDate),
        time: selectedTime,
        client_name, client_phone,
        client_email,
        birthdate: birthdate || null,
        age: age || null,
        gender: gender || null,
        address: address || null,
        city: city || null,
        emergency_contact: emergency_contact || null,
        notes,
        clinical_form: clinical,
      });
      setConfirmed(data);
      if (data.payment_required && data.appointment_id) {
        setPaymentAppointmentId(data.appointment_id);
        setShowPaymentStep(true);
        await createMercadoPagoPreference(data.appointment_id);
        return;
      }
      setStep(5);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No pudimos crear tu reserva. Intenta otro horario.");
    } finally {
      setSubmitting(false);
    }
  };

  const paymentActionLabel = submitting
    ? "Confirmando…"
    : showPaymentStep
      ? paymentProcessing
        ? "Redirigiendo al pago…"
        : "Reintentar pago"
      : "Confirmar reserva";
  const paymentActionDisabled = submitting || paymentProcessing;
  const showPaymentFooterButton = !showPaymentStep;

  return (
    <div className="min-h-screen bg-[#F4EFE4]">
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-[#F4EFE4]/90 border-b border-[#E8DFD2]">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="booking-logo">
            <img src={LOGO_URL} alt="Amarme" className="h-10 w-10 rounded-full object-cover border border-[#B5704A]/20" />
            <div className="leading-none">
              <div className="brand-wordmark text-base text-[#3A2418]">AMARME</div>
              <div className="font-script text-xs text-[#B5704A] -mt-0.5">Skin</div>
            </div>
          </Link>
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-[#6F5A4C] hover:text-[#B5704A] transition-colors" data-testid="back-home">
            <ArrowLeft className="w-4 h-4" /> Volver
          </Link>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-6 py-12">
        {step < 5 && (
          <div className="mb-10 flex items-center justify-between">
            {[
              { n: 1, label: "Tratamiento" },
              { n: 2, label: "Fecha" },
              { n: 3, label: "Hora" },
              { n: 4, label: "Ficha" },
            ].map((s, i, arr) => (
              <div key={s.n} className="flex items-center flex-1">
                <div className={`flex items-center gap-3 ${step >= s.n ? "text-[#B5704A]" : "text-[#6F5A4C]/40"}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium border-2 transition-colors ${step > s.n ? "bg-[#B5704A] border-[#B5704A] text-white" : step === s.n ? "border-[#B5704A] text-[#B5704A]" : "border-[#E8DFD2] text-[#6F5A4C]/40"}`}>
                    {step > s.n ? <Check className="w-4 h-4" /> : s.n}
                  </div>
                  <span className="text-sm font-medium hidden sm:inline">{s.label}</span>
                </div>
                {i < arr.length - 1 && (<div className={`flex-1 h-px mx-2 ${step > s.n ? "bg-[#B5704A]" : "bg-[#E8DFD2]"}`}></div>)}
              </div>
            ))}
          </div>
        )}

        <AnimatePresence mode="wait">
          {/* STEP 1: Service */}
          {step === 1 && (
            <motion.div key="step-1" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.3 }}>
              <span className="label-eyebrow text-[#B5704A]">Paso 1</span>
              <h1 className="font-display text-4xl sm:text-5xl font-[300] text-[#3A2418] mt-2">
                ¿Qué tratamiento <span className="italic font-[500] text-[#B5704A]">te gustaría?</span>
              </h1>
              <div className="grid gap-3 mt-8">
                {services.map((s) => (
                  <button key={s.slug} onClick={() => { setService(s); next(); }} className={`text-left surface-card surface-card-hover p-5 flex items-center gap-4 ${service?.slug === s.slug ? "border-[#B5704A] ring-2 ring-[#B5704A]/20" : ""}`} data-testid={`service-option-${s.slug}`}>
                    <div className="w-10 h-10 rounded-full bg-[#B5704A]/10 flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-[#B5704A]" />
                    </div>
                    <div className="flex-1">
                      <div className="font-display text-xl font-[500] text-[#3A2418]">{s.title}</div>
                      <div className="text-xs text-[#6F5A4C] flex items-center gap-1 mt-1"><Clock className="w-3 h-3" /> {s.minutes} minutos</div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-[#B5704A]" />
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* STEP 2: Date */}
          {step === 2 && (
            <motion.div key="step-2" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.3 }}>
              <span className="label-eyebrow text-[#B5704A]">Paso 2</span>
              <h1 className="font-display text-4xl sm:text-5xl font-[300] text-[#3A2418] mt-2">
                Elige el <span className="italic font-[500] text-[#B5704A]">día</span>
              </h1>
              <p className="text-sm text-[#6F5A4C] mt-2">Servicio elegido: <strong className="text-[#3A2418]">{service?.title}</strong></p>
              <div className="surface-card mt-8 p-6">
                <div className="flex items-center justify-between mb-6">
                  <Button variant="ghost" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} data-testid="prev-month"><ArrowLeft className="w-4 h-4" /></Button>
                  <div className="font-display text-xl text-[#3A2418]">{MONTH_NAMES[month.getMonth()]} {month.getFullYear()}</div>
                  <Button variant="ghost" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} data-testid="next-month"><ArrowRight className="w-4 h-4" /></Button>
                </div>
                <div className="grid grid-cols-7 gap-1 text-center">
                  {DAY_NAMES.map((d) => (<div key={d} className="label-eyebrow text-[10px] text-[#6F5A4C] py-2">{d}</div>))}
                  {monthDays.map((d, i) => {
                    if (!d) return <div key={i}></div>;
                    const disabled = isDisabled(d);
                    const isSelected = selectedDate && isoDate(selectedDate) === isoDate(d);
                    return (
                      <button key={i} disabled={disabled} onClick={() => { setSelectedDate(d); setSelectedTime(null); }}
                        className={`aspect-square rounded-lg flex items-center justify-center text-sm transition-all
                          ${disabled ? "text-[#6F5A4C]/30 cursor-not-allowed line-through" : "hover:bg-[#E8DFD2]/50 text-[#3A2418]"}
                          ${isSelected ? "bg-[#B5704A] text-white hover:bg-[#9C5E3D]" : ""}`}
                        data-testid={`day-${isoDate(d)}`}>{d.getDate()}</button>
                    );
                  })}
                </div>
                <p className="text-xs text-[#6F5A4C] mt-4 italic">* Domingos cerrados · Sábados hasta 15:00</p>
              </div>
              <div className="flex justify-between mt-6">
                <Button variant="ghost" onClick={back} data-testid="back-step"><ArrowLeft className="w-4 h-4 mr-2" /> Atrás</Button>
                <Button onClick={next} disabled={!selectedDate} className="bg-[#B5704A] hover:bg-[#9C5E3D] hover:text-white text-white rounded-full" data-testid="continue-step">Continuar <ArrowRight className="w-4 h-4 ml-2" /></Button>
              </div>
            </motion.div>
          )}

          {/* STEP 3: Time */}
          {step === 3 && (
            <motion.div key="step-3" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.3 }}>
              <span className="label-eyebrow text-[#B5704A]">Paso 3</span>
              <h1 className="font-display text-4xl sm:text-5xl font-[300] text-[#3A2418] mt-2">Elige tu <span className="italic font-[500] text-[#B5704A]">hora</span></h1>
              <p className="text-sm text-[#6F5A4C] mt-2">{selectedDate && selectedDate.toLocaleDateString("es", { weekday: "long", day: "numeric", month: "long" })}</p>
              <div className="surface-card mt-8 p-6 min-h-[200px]">
                {loadingSlots ? (<div className="text-center text-[#6F5A4C] py-8">Cargando horarios disponibles…</div>)
                  : slots.length === 0 ? (<div className="text-center text-[#6F5A4C] py-8">No hay horarios disponibles este día.</div>)
                  : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {slots.map((s) => (
                        <button key={s.time} disabled={!s.available} onClick={() => setSelectedTime(s.time)}
                          className={`py-3 rounded-lg text-sm font-medium transition-all
                            ${!s.available ? "bg-[#E8DFD2]/30 text-[#6F5A4C]/40 cursor-not-allowed line-through" : ""}
                            ${selectedTime === s.time ? "bg-[#B5704A] text-white" : s.available ? "bg-white border border-[#E8DFD2] text-[#3A2418] hover:border-[#B5704A]" : ""}`}
                          data-testid={`slot-${s.time}`}>{s.time}</button>
                      ))}
                    </div>
                  )}
              </div>
              <div className="flex justify-between mt-6">
                <Button variant="ghost" onClick={back}><ArrowLeft className="w-4 h-4 mr-2" /> Atrás</Button>
                <Button onClick={next} disabled={!selectedTime} className="bg-[#B5704A] hover:bg-[#9C5E3D] hover:text-white text-white rounded-full" data-testid="continue-step-3">Continuar <ArrowRight className="w-4 h-4 ml-2" /></Button>
              </div>
            </motion.div>
          )}

          {/* STEP 4: Clinical Form */}
          {step === 4 && (
            <motion.div key="step-4" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.3 }}>
              <span className="label-eyebrow text-[#B5704A]">Paso 4</span>
              <h1 className="font-display text-4xl sm:text-5xl font-[300] text-[#3A2418] mt-2">
                Ficha <span className="italic font-[500] text-[#B5704A]">clínica</span>
              </h1>
              <p className="text-sm text-[#6F5A4C] mt-2">Para brindarte el mejor cuidado, necesitamos conocerte un poco. Los campos con * son obligatorios.</p>

              {/* Summary card */}
              <div className="surface-card p-5 mt-6 bg-[#FAF7F1]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#B5704A]/10 flex items-center justify-center"><CalendarHeart className="w-5 h-5 text-[#B5704A]" /></div>
                  <div>
                    <div className="font-medium text-[#3A2418]">{service?.title}</div>
                    <div className="text-sm text-[#6F5A4C]">{selectedDate?.toLocaleDateString("es", { weekday: "long", day: "numeric", month: "long" })} · {selectedTime} · {service?.minutes} min</div>
                  </div>
                </div>
              </div>

              {/* Información del cliente */}
              <div className="surface-card p-6 mt-4">
                <SectionTitle>Información del cliente</SectionTitle>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2">
                    <Label className="text-sm">Nombre completo *</Label>
                    <Input value={form.client_name} onChange={(e) => update("client_name", e.target.value)} className="mt-1.5 bg-white border-[#E8DFD2]" data-testid="booking-name" />
                  </div>
                  <div>
                    <Label className="text-sm">WhatsApp *</Label>
                    <Input value={form.client_phone} onChange={(e) => update("client_phone", e.target.value)} placeholder="+56 9 ..." className="mt-1.5 bg-white border-[#E8DFD2]" data-testid="booking-phone" />
                  </div>
                  <div>
                    <Label className="text-sm">Correo electrónico *</Label>
                    <Input type="email" required value={form.client_email} onChange={(e) => update("client_email", e.target.value)} placeholder="tu@correo.com" className="mt-1.5 bg-white border-[#E8DFD2]" data-testid="booking-email" />
                    <p className="text-xs text-[#6F5A4C] mt-1">Te enviaremos la confirmación de tu cita aquí</p>
                  </div>
                  <div>
                    <Label className="text-sm">Fecha de nacimiento</Label>
                    <Input type="date" value={form.birthdate} onChange={(e) => update("birthdate", e.target.value)} className="mt-1.5 bg-white border-[#E8DFD2]" />
                  </div>
                  <div>
                    <Label className="text-sm">Edad</Label>
                    <Input type="number" value={form.age} onChange={(e) => update("age", e.target.value)} className="mt-1.5 bg-white border-[#E8DFD2]" />
                  </div>
                  <div>
                    <Label className="text-sm">Género</Label>
                    <RadioGroup value={form.gender} onValueChange={(v) => update("gender", v)} className="flex gap-4 mt-2" data-testid="gender-group">
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="Femenino" id="gen-f" className="border-[#B5704A] text-[#B5704A]" />
                        <Label htmlFor="gen-f" className="text-sm cursor-pointer">Femenino</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="Masculino" id="gen-m" className="border-[#B5704A] text-[#B5704A]" />
                        <Label htmlFor="gen-m" className="text-sm cursor-pointer">Masculino</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="Otro" id="gen-o" className="border-[#B5704A] text-[#B5704A]" />
                        <Label htmlFor="gen-o" className="text-sm cursor-pointer">Otro / Prefiero no decir</Label>
                      </div>
                    </RadioGroup>
                  </div>
                  <div>
                    <Label className="text-sm">Ciudad</Label>
                    <Input value={form.city} onChange={(e) => update("city", e.target.value)} className="mt-1.5 bg-white border-[#E8DFD2]" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-sm">Dirección</Label>
                    <Input value={form.address} onChange={(e) => update("address", e.target.value)} className="mt-1.5 bg-white border-[#E8DFD2]" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-sm">Contacto de emergencia</Label>
                    <Input value={form.emergency_contact} onChange={(e) => update("emergency_contact", e.target.value)} placeholder="Nombre y teléfono" className="mt-1.5 bg-white border-[#E8DFD2]" />
                  </div>
                </div>

                <div className="mt-5">
                  <Label className="text-sm">¿Cómo se enteró de nosotros?</Label>
                  <RadioGroup value={form.heard_from} onValueChange={(v) => update("heard_from", v)} className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                    {HEARD_FROM.map((opt) => (
                      <div key={opt} className="flex items-center gap-2">
                        <RadioGroupItem value={opt} id={`heard-${opt}`} className="border-[#B5704A] text-[#B5704A]" />
                        <Label htmlFor={`heard-${opt}`} className="text-sm cursor-pointer">{opt}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                  {form.heard_from === "Otro" && (
                    <Input value={form.heard_from_other} onChange={(e) => update("heard_from_other", e.target.value)} placeholder="Especifica" className="mt-2 bg-white border-[#E8DFD2]" />
                  )}
                </div>

                <label className="flex items-center gap-2 mt-5 cursor-pointer">
                  <Checkbox checked={form.marketing_optin} onCheckedChange={(v) => update("marketing_optin", !!v)} className="border-[#B5704A] data-[state=checked]:bg-[#B5704A]" />
                  <span className="text-sm text-[#6F5A4C]">Quiero recibir noticias y ofertas exclusivas por email</span>
                </label>
              </div>

              {/* Historial médico */}
              <div className="surface-card p-6 mt-4">
                <SectionTitle>Historial médico</SectionTitle>
                <div className="space-y-5">
                  <div>
                    <Label className="text-sm">¿Está tomando algún medicamento, vitamina o fármaco con receta?</Label>
                    <YesNo value={form.takes_medication} onChange={(v) => update("takes_medication", v)} name="med" />
                    {form.takes_medication === "si" && (
                      <Input value={form.medication_detail} onChange={(e) => update("medication_detail", e.target.value)} placeholder="¿Cuáles?" className="mt-2 bg-white border-[#E8DFD2]" />
                    )}
                  </div>
                  <div>
                    <Label className="text-sm">¿Alguna alergia conocida?</Label>
                    <YesNo value={form.has_allergies} onChange={(v) => update("has_allergies", v)} name="alergias" />
                    {form.has_allergies === "si" && (
                      <Input value={form.allergies_detail} onChange={(e) => update("allergies_detail", e.target.value)} placeholder="Especifica" className="mt-2 bg-white border-[#E8DFD2]" />
                    )}
                  </div>
                  <div>
                    <Label className="text-sm">¿Alguna cirugía reciente, incluyendo cirugía plástica?</Label>
                    <YesNo value={form.recent_surgery} onChange={(v) => update("recent_surgery", v)} name="cirugia" />
                    {form.recent_surgery === "si" && (
                      <Input value={form.surgery_detail} onChange={(e) => update("surgery_detail", e.target.value)} placeholder="Especifica" className="mt-2 bg-white border-[#E8DFD2]" />
                    )}
                  </div>
                  <div>
                    <Label className="text-sm">¿Estás embarazada o intentando quedarte embarazada?</Label>
                    <YesNo value={form.pregnant} onChange={(v) => update("pregnant", v)} name="embarazo" />
                  </div>
                  <div>
                    <Label className="text-sm">¿Estás tomando anticonceptivos?</Label>
                    <YesNo value={form.birth_control} onChange={(v) => update("birth_control", v)} name="anticon" />
                  </div>
                  <div>
                    <Label className="text-sm">¿Recibes terapia de reemplazo hormonal?</Label>
                    <YesNo value={form.hormone_therapy} onChange={(v) => update("hormone_therapy", v)} name="hormonal" />
                  </div>
                  <div>
                    <Label className="text-sm">¿Has usado isotretinoína (Roacután, Accutane u otro) en los últimos 6 a 12 meses?</Label>
                    <YesNo value={form.isotretinoin_recent} onChange={(v) => update("isotretinoin_recent", v)} name="isotret" />
                    {form.isotretinoin_recent === "si" && (
                      <Input value={form.isotretinoin_detail} onChange={(e) => update("isotretinoin_detail", e.target.value)} placeholder="¿Cuándo y por cuánto tiempo?" className="mt-2 bg-white border-[#E8DFD2]" data-testid="isotret-detail" />
                    )}
                  </div>
                  <div>
                    <Label className="text-sm">¿Tienes implantes metálicos, marcapasos u otro dispositivo médico?</Label>
                    <YesNo value={form.has_implants} onChange={(v) => update("has_implants", v)} name="implants" />
                    {form.has_implants === "si" && (
                      <Input value={form.implants_detail} onChange={(e) => update("implants_detail", e.target.value)} placeholder="Especifica qué tipo y ubicación" className="mt-2 bg-white border-[#E8DFD2]" data-testid="implants-detail" />
                    )}
                  </div>
                </div>
              </div>

              {/* Afecciones */}
              <div className="surface-card p-6 mt-4">
                <SectionTitle>¿Presenta alguna de las siguientes afecciones?</SectionTitle>
                <p className="text-xs text-[#6F5A4C] -mt-2 mb-2">Marca todas las que correspondan</p>
                <CheckGrid options={CONDITIONS} selected={form.conditions} onChange={(v) => update("conditions", v)} name="cond" />
                <div className="mt-4">
                  <Label className="text-sm">Otro</Label>
                  <Input value={form.conditions_other} onChange={(e) => update("conditions_other", e.target.value)} className="mt-1.5 bg-white border-[#E8DFD2]" />
                </div>
              </div>

              {/* Preocupaciones cutáneas */}
              <div className="surface-card p-6 mt-4">
                <SectionTitle>¿Cuáles son tus principales preocupaciones sobre la piel?</SectionTitle>
                <CheckGrid options={SKIN_CONCERNS} selected={form.skin_concerns} onChange={(v) => update("skin_concerns", v)} name="conc" />

                {form.skin_concerns.some((c) => c.toLowerCase().includes("acné") || c.toLowerCase().includes("acne")) && (
                  <div className="mt-6 pt-5 border-t border-dashed border-[#E8DFD2] space-y-4" data-testid="acne-detail-block">
                    <div className="font-display text-lg text-[#3A2418]">Sobre tu acné</div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-sm">Edad de inicio</Label>
                        <Input value={form.acne_age} onChange={(e) => update("acne_age", e.target.value)} placeholder="Ej. 14 años" className="mt-1.5 bg-white border-[#E8DFD2]" data-testid="acne-age" />
                      </div>
                      <div>
                        <Label className="text-sm">Localización</Label>
                        <Input value={form.acne_location} onChange={(e) => update("acne_location", e.target.value)} placeholder="Frente, mejillas, mentón…" className="mt-1.5 bg-white border-[#E8DFD2]" data-testid="acne-location" />
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-sm">Tratamientos previos</Label>
                        <Input value={form.acne_treatments} onChange={(e) => update("acne_treatments", e.target.value)} placeholder="Medicamentos, dermatólogo, productos…" className="mt-1.5 bg-white border-[#E8DFD2]" data-testid="acne-treatments" />
                      </div>
                    </div>
                  </div>
                )}

                {form.skin_concerns.some((c) => c.toLowerCase().includes("mancha") || c.toLowerCase().includes("pigment")) && (
                  <div className="mt-6 pt-5 border-t border-dashed border-[#E8DFD2] space-y-4" data-testid="manchas-detail-block">
                    <div className="font-display text-lg text-[#3A2418]">Sobre tus manchas</div>
                    <div>
                      <Label className="text-sm">Las manchas aparecieron por:</Label>
                      <CheckGrid
                        options={["Sol", "Embarazo", "Acné", "Medicamentos", "Otra causa"]}
                        selected={form.manchas_cause}
                        onChange={(v) => update("manchas_cause", v)}
                        name="manchas-cause"
                      />
                      {form.manchas_cause.includes("Otra causa") && (
                        <Input value={form.manchas_cause_other} onChange={(e) => update("manchas_cause_other", e.target.value)} placeholder="Especifica" className="mt-2 bg-white border-[#E8DFD2]" data-testid="manchas-cause-other" />
                      )}
                    </div>
                    <div>
                      <Label className="text-sm">¿Tienes antecedentes familiares de melasma?</Label>
                      <YesNo value={form.manchas_melasma_family} onChange={(v) => update("manchas_melasma_family", v)} name="manchas-melasma" />
                    </div>
                    <div>
                      <Label className="text-sm">¿Las manchas empeoran en verano?</Label>
                      <YesNo value={form.manchas_worse_summer} onChange={(v) => update("manchas_worse_summer", v)} name="manchas-summer" />
                    </div>
                  </div>
                )}
              </div>

              {/* Alergias a componentes */}
              <div className="surface-card p-6 mt-4">
                <SectionTitle>¿Has tenido reacción alérgica a alguno de estos?</SectionTitle>
                <CheckGrid options={ALLERGY_INGREDIENTS} selected={form.allergy_ingredients} onChange={(v) => update("allergy_ingredients", v)} name="aller" />
                <div className="mt-4">
                  <Label className="text-sm">Otro / Especifica</Label>
                  <Input value={form.allergy_ingredients_other} onChange={(e) => update("allergy_ingredients_other", e.target.value)} className="mt-1.5 bg-white border-[#E8DFD2]" />
                </div>
              </div>

              {/* Tipo de piel */}
              <div className="surface-card p-6 mt-4">
                <SectionTitle>¿Qué tipo de piel tienes?</SectionTitle>
                <RadioGroup value={form.skin_type} onValueChange={(v) => update("skin_type", v)} className="grid grid-cols-2 gap-2 mt-2">
                  {SKIN_TYPES.map((t) => (
                    <div key={t} className="flex items-center gap-2">
                      <RadioGroupItem value={t} id={`st-${t}`} className="border-[#B5704A] text-[#B5704A]" />
                      <Label htmlFor={`st-${t}`} className="text-sm cursor-pointer">{t}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              {/* Estado actual de la piel */}
              <div className="surface-card p-6 mt-4">
                <SectionTitle>¿Cómo se encuentra tu piel en este momento?</SectionTitle>
                <CheckGrid options={SKIN_STATES} selected={form.skin_state} onChange={(v) => update("skin_state", v)} name="state" />
              </div>

              {/* Rutina */}
              <div className="surface-card p-6 mt-4">
                <SectionTitle>Tu rutina de cuidado</SectionTitle>
                <p className="text-xs text-[#6F5A4C] -mt-2 mb-3">Indica marca y nombre del producto si lo recuerdas</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { key: "routine_makeup_remover", label: "Desmaquillante" },
                    { key: "routine_cleanser", label: "Limpiador en gel" },
                    { key: "routine_serum", label: "Suero" },
                    { key: "routine_exfoliant", label: "Exfoliante" },
                    { key: "routine_sunscreen", label: "Protector solar" },
                    { key: "routine_other", label: "Otro" },
                  ].map((r) => (
                    <div key={r.key}>
                      <Label className="text-sm">{r.label}</Label>
                      <Input value={form[r.key]} onChange={(e) => update(r.key, e.target.value)} className="mt-1.5 bg-white border-[#E8DFD2]" />
                    </div>
                  ))}
                </div>
              </div>

              {/* Hábitos y tratamientos previos */}
              <div className="surface-card p-6 mt-4">
                <SectionTitle>Hábitos y tratamientos previos</SectionTitle>
                <div className="space-y-5">
                  <div>
                    <Label className="text-sm">¿Fumas o consumes alcohol?</Label>
                    <YesNo value={form.smokes_drinks} onChange={(v) => update("smokes_drinks", v)} name="fumar" />
                  </div>
                  <div>
                    <Label className="text-sm">¿Has probado inyecciones de Botox, Ácido hialurónico u otro?</Label>
                    <YesNo value={form.has_injections} onChange={(v) => update("has_injections", v)} name="iny" />
                    {form.has_injections === "si" && (
                      <Input value={form.injections_detail} onChange={(e) => update("injections_detail", e.target.value)} placeholder="Especifica cuáles y cuándo" className="mt-2 bg-white border-[#E8DFD2]" />
                    )}
                  </div>
                  <div>
                    <Label className="text-sm">¿Has recibido recientemente alguno de estos tratamientos?</Label>
                    <CheckGrid options={RECENT_TREATMENTS} selected={form.recent_treatments} onChange={(v) => update("recent_treatments", v)} name="treat" />
                  </div>
                  <div>
                    <Label className="text-sm">¿Cuándo fue la fecha del último tratamiento estético realizado?</Label>
                    <Input value={form.last_aesthetic_date} onChange={(e) => update("last_aesthetic_date", e.target.value)} placeholder="Ej. Marzo 2026 o '3 meses atrás'" className="mt-1.5 bg-white border-[#E8DFD2]" data-testid="last-aesthetic-date" />
                  </div>
                </div>
              </div>

              {/* Estilo de vida */}
              <div className="surface-card p-6 mt-4">
                <SectionTitle>Estilo de vida</SectionTitle>
                <div className="space-y-5">
                  <div>
                    <Label className="text-sm">Calidad del sueño</Label>
                    <RadioGroup value={form.sleep_quality} onValueChange={(v) => update("sleep_quality", v)} className="grid grid-cols-5 gap-2 mt-2" data-testid="sleep-quality-group">
                      {["1", "2", "3", "4", "5"].map((n) => (
                        <label key={n} className="flex flex-col items-center gap-1 cursor-pointer">
                          <RadioGroupItem value={n} id={`sleep-${n}`} className="border-[#B5704A] text-[#B5704A]" />
                          <span className="text-xs text-[#6F5A4C]">{n}</span>
                        </label>
                      ))}
                    </RadioGroup>
                    <div className="flex justify-between text-[10px] text-[#6F5A4C] mt-1 px-1">
                      <span>Muy mala</span><span>Excelente</span>
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm">Nivel de estrés</Label>
                    <RadioGroup value={form.stress_level} onValueChange={(v) => update("stress_level", v)} className="grid grid-cols-5 gap-2 mt-2" data-testid="stress-level-group">
                      {["1", "2", "3", "4", "5"].map((n) => (
                        <label key={n} className="flex flex-col items-center gap-1 cursor-pointer">
                          <RadioGroupItem value={n} id={`stress-${n}`} className="border-[#B5704A] text-[#B5704A]" />
                          <span className="text-xs text-[#6F5A4C]">{n}</span>
                        </label>
                      ))}
                    </RadioGroup>
                    <div className="flex justify-between text-[10px] text-[#6F5A4C] mt-1 px-1">
                      <span>Muy bajo</span><span>Muy alto</span>
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm">Actividad física</Label>
                    <RadioGroup value={form.physical_activity} onValueChange={(v) => update("physical_activity", v)} className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2" data-testid="physical-activity-group">
                      {["Sedentaria", "Ligera (1-2 v/sem)", "Moderada (3-4 v/sem)", "Intensa (5+ v/sem)"].map((opt) => (
                        <div key={opt} className="flex items-center gap-2">
                          <RadioGroupItem value={opt} id={`pa-${opt}`} className="border-[#B5704A] text-[#B5704A]" />
                          <Label htmlFor={`pa-${opt}`} className="text-xs cursor-pointer">{opt}</Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>
                  <div>
                    <Label className="text-sm">Exposición solar</Label>
                    <RadioGroup value={form.sun_exposure} onValueChange={(v) => update("sun_exposure", v)} className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2" data-testid="sun-exposure-group">
                      {["Mínima", "Ocasional", "Frecuente", "Diaria sin protección"].map((opt) => (
                        <div key={opt} className="flex items-center gap-2">
                          <RadioGroupItem value={opt} id={`sun-${opt}`} className="border-[#B5704A] text-[#B5704A]" />
                          <Label htmlFor={`sun-${opt}`} className="text-xs cursor-pointer">{opt}</Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>
                </div>
              </div>

              {/* Expectativas */}
              <div className="surface-card p-6 mt-4">
                <SectionTitle>Expectativas y objetivos</SectionTitle>
                <Label className="text-sm">¿Cuáles son tus expectativas y objetivos con este tratamiento?</Label>
                <Textarea value={form.expectations} onChange={(e) => update("expectations", e.target.value)} rows={3} className="mt-1.5 bg-white border-[#E8DFD2]" />
                <div className="mt-4">
                  <Label className="text-sm">¿Algo más que debamos saber?</Label>
                  <Textarea value={form.notes} onChange={(e) => update("notes", e.target.value)} placeholder="Notas adicionales..." rows={2} className="mt-1.5 bg-white border-[#E8DFD2]" />
                </div>
              </div>

              {/* Consentimiento */}
              <div className="surface-card p-6 mt-4 bg-[#FAF7F1]">
                <SectionTitle>Declaración de consentimiento</SectionTitle>
                <p className="text-sm text-[#6F5A4C] leading-relaxed">
                  Al marcar a continuación, declaro que he leído y comprendo las preguntas anteriores
                  y que he declarado todas las afecciones relevantes que me afectan. Es mi responsabilidad
                  informar al técnico sobre cualquier cambio en mi historial médico o estado de salud,
                  tanto ahora como en el futuro, para garantizar mi seguridad y la correcta atención que recibo.
                </p>
                <label className="flex items-start gap-3 mt-4 cursor-pointer">
                  <Checkbox checked={form.consent} onCheckedChange={(v) => update("consent", !!v)} className="border-[#B5704A] data-[state=checked]:bg-[#B5704A] mt-0.5" data-testid="consent-checkbox" />
                  <span className="text-sm text-[#3A2418] font-medium">Acepto la declaración de consentimiento *</span>
                </label>
              </div>

              {depositStatus.enabled && !depositStatus.loading && (
                <div className="rounded-3xl border border-[#E8DFD2] bg-[#FAF7F1] p-4 text-sm text-[#3A2418] mb-4">
                  Esta reserva requiere un abono de <strong>${depositStatus.amount_clp.toLocaleString("es-CL")}</strong>. Después de confirmar la reserva, serás redirigido a Mercado Pago para completar el pago.
                </div>
              )}
              <div className="flex justify-between mt-6">
                <Button variant="ghost" onClick={back}><ArrowLeft className="w-4 h-4 mr-2" /> Atrás</Button>
                {showPaymentFooterButton && (
                  <Button onClick={handleSubmit} disabled={paymentActionDisabled} className="bg-[#B5704A] hover:bg-[#9C5E3D] hover:text-white text-white rounded-full px-8" data-testid="booking-confirm">
                    {paymentActionLabel}
                  </Button>
                )}
              </div>
            </motion.div>
          )}
{showPaymentStep && (
  <motion.div key="payment-step" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.4 }} className="mt-8">
    <div className="max-w-md mx-auto">
      {/* Card decorativo */}
      <div className="relative">
        <div className="absolute -inset-1 bg-gradient-to-br from-[#B5704A]/20 to-[#E8DFD2]/20 rounded-[2rem] blur-lg"></div>
        <div className="relative surface-card p-8 rounded-[2rem] bg-white border border-[#E8DFD2]">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-14 h-14 mx-auto rounded-full bg-gradient-to-br from-[#B5704A]/10 to-[#B5704A]/5 flex items-center justify-center mb-4">
              <svg className="w-7 h-7 text-[#B5704A]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h10m4 0a1 1 0 11-2 0 1 1 0 012 0z" />
              </svg>
            </div>
            <h2 className="font-display text-2xl font-[500] text-[#3A2418] mb-2">
              Abono de Reserva
            </h2>
            <p className="text-xs uppercase tracking-widest text-[#B5704A] font-medium">Paso final</p>
          </div>

          {/* Descripción */}
          <p className="text-sm text-[#6F5A4C] mb-6 text-center leading-relaxed">
            {paymentProcessing
              ? "Estamos preparando tu pago en Mercado Pago. Si el proceso no inicia automáticamente, revisa tu conexión o intenta nuevamente."
              : "Serás redirigido a Mercado Pago para completar el abono de reserva."}
          </p>

          {/* Status box */}
          <div className="rounded-2xl border border-[#E8DFD2] bg-gradient-to-br from-[#FAF7F1] to-[#F4EFE4] p-5 mb-4">
            <div className="text-center text-sm text-[#3A2418]">
              {paymentProcessing ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-2 h-2 bg-[#B5704A] rounded-full animate-pulse"></div>
                  <span>Redirigiendo al pago seguro...</span>
                </div>
              ) : (
                <div className="space-y-1">
                  <p className="font-medium">Listo para pagar</p>
                  <p className="text-xs text-[#6F5A4C]">El sistema está preparado para llevarte al pago seguro de Mercado Pago.</p>
                </div>
              )}
            </div>
          </div>

          {/* Aviso */}
          <p className="text-xs text-[#6F5A4C] text-center mb-6 leading-relaxed">
            Tu reserva ya está creada y está a la espera del abono. <strong>No crees otra reserva</strong> para esta misma fecha y hora.
          </p>

          {/* Error message */}
          {paymentRedirectError && (
            <div className="mb-6 rounded-xl border border-[#F5D4D4] bg-gradient-to-br from-[#FBEFF0] to-[#F4EFE4] p-4">
              <p className="text-sm text-[#A82E2E] flex items-start gap-2">
                <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span>{paymentRedirectError}</span>
              </p>
            </div>
          )}

          {/* Button */}
          <Button 
            onClick={retryPayment} 
            disabled={paymentProcessing || submitting} 
            className="w-full bg-gradient-to-r from-[#B5704A] to-[#9C5E3D] hover:from-[#9C5E3D] hover:to-[#8B523A] text-white rounded-full py-3 font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed" 
            data-testid="payment-retry-button"
          >
            {paymentActionLabel}
          </Button>
        </div>
      </div>

      {/* Seguridad badge */}
      <div className="mt-4 text-center">
        <p className="text-xs text-[#6F5A4C] flex items-center justify-center gap-1.5">
          <svg className="w-4 h-4 text-[#B5704A]" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
          </svg>
          Pago seguro con Mercado Pago
        </p>
      </div>
    </div>
  </motion.div>
)}

          {/* STEP 5: Confirmation */}
          {step === 5 && confirmed && (
            <motion.div key="step-5" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }} className="text-center py-12">
              <div className="w-20 h-20 rounded-full bg-[#B5704A] flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-white" />
              </div>
              <span className="label-eyebrow text-[#B5704A]">Agendamiento confirmado</span>
              <h1 className="font-display text-4xl sm:text-5xl font-[300] text-[#3A2418] mt-3" data-testid="booking-success">
                ¡Te <span className="italic font-[500] text-[#B5704A]">esperamos!</span>
              </h1>
              <div className="surface-card p-6 mt-8 max-w-md mx-auto text-left">
                <div className="text-xs text-[#6F5A4C] uppercase tracking-widest">Tu cita</div>
                <div className="font-display text-2xl font-[500] text-[#3A2418] mt-2">{confirmed.service}</div>
                <div className="text-sm text-[#6F5A4C] mt-2">
                  {new Date(confirmed.start).toLocaleDateString("es-CL", { timeZone: "America/Santiago", weekday: "long", day: "numeric", month: "long", year: "numeric" })}<br />
                  {new Date(confirmed.start).toLocaleTimeString("es-CL", { timeZone: "America/Santiago", hour: "2-digit", minute: "2-digit" })} hrs · {confirmed.duration_min} minutos
                </div>
              </div>
              <p className="text-sm text-[#6F5A4C] mt-8 max-w-md mx-auto">
                Te enviaremos un recordatorio por WhatsApp el día anterior. Si necesitas reprogramar, escríbenos al <a href="https://wa.me/56973842894" className="text-[#B5704A] underline">+56 9 7384 2894</a>.
              </p>
              <Link to="/"><Button className="mt-8 bg-[#B5704A] hover:bg-[#9C5E3D] hover:text-white text-white rounded-full">Volver al sitio</Button></Link>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
